import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WSMessage {
  id?: string;
  role: "user" | "assistant" | "system";
  content: string;
  tool_calls?: any;
  attachments?: any[];
  created_at?: string;
}

export interface WSFile {
  id: string;
  file_name: string;
  file_type: string | null;
  storage_path: string;
  category: string | null;
  vision_summary: string | null;
  extracted_data: any;
}

const buildFileReadyMessage = (file: WSFile, summary?: string | null) => {
  const isBlueprint = file.category === "blueprint" || file.extracted_data?.document_type === "blueprint";
  const blueprint = file.extracted_data?.blueprint_analysis;
  const tradeScopes = file.extracted_data?.trade_breakdown ?? blueprint?.trade_scopes;
  const pricingSummary = file.extracted_data?.pricing_summary ?? blueprint?.pricing_summary;
  const blueprintDetails = isBlueprint && blueprint
    ? `\n\nBlueprint pass:\n- Sheet: ${blueprint.sheet_number || "not found"}${blueprint.sheet_title ? ` — ${blueprint.sheet_title}` : ""}\n- Sheet count: ${blueprint.sheet_count || "not found"}\n- Trades found: ${Array.isArray(tradeScopes) ? tradeScopes.length : 0}\n- Dimensions found: ${Array.isArray(blueprint.dimensions) ? blueprint.dimensions.length : 0}\n- Scope items found: ${Array.isArray(blueprint.takeoff_items) ? blueprint.takeoff_items.length : 0}\n- Priced rows found: ${typeof pricingSummary?.priced_items_count === "number" ? pricingSummary.priced_items_count : 0}\n- Unreadable zones: ${Array.isArray(blueprint.unreadable_zones) ? blueprint.unreadable_zones.length : 0}`
    : "";
  const details = summary?.trim()
    ? `\n\nI built a structured ${isBlueprint ? "blueprint review" : "scope review"} from what I could read. Open the breakdown card below for totals, line items, paid/incurred notes, and next options.${blueprintDetails}\n\nSummary:\n${summary.trim()}`
    : "";

  return `I analyzed ${file.file_name}.${details}\n\nTell me what you want next — I can draft an estimate, pull claim totals, write the adjuster email, create a job, or push this into an existing job.`;
};

const buildScopeReviewAttachment = (file: WSFile, summary?: string | null) => ({
  type: "bridgette_scope_review",
  fileName: file.file_name,
  category: file.category,
  summary: summary ?? file.vision_summary,
  extracted: file.extracted_data ?? {},
});

export function useBridgetteWorkspace(orgId: string | null, userId: string | null) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WSMessage[]>([]);
  const [files, setFiles] = useState<WSFile[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [threadInitializing, setThreadInitializing] = useState(false);
  const [workspaceStatus, setWorkspaceStatus] = useState("Waiting for workspace context…");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Create or load active thread
  useEffect(() => {
    if (!orgId || !userId) {
      setWorkspaceStatus("Waiting for organization…");
      return;
    }
    (async () => {
      setThreadInitializing(true);
      setWorkspaceStatus("Preparing workspace…");
      const { data: existing } = await supabase
        .from("bridgette_workspace_threads")
        .select("*")
        .eq("org_id", orgId)
        .eq("created_by", userId)
        .eq("status", "active")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        setThreadId(existing.id);
        await loadThread(existing.id);
        setWorkspaceStatus("Ready for blueprints, scopes, claims, and photos.");
        setThreadInitializing(false);
        return;
      }

      const { data: created, error: createErr } = await supabase
        .from("bridgette_workspace_threads")
        .insert({ org_id: orgId, created_by: userId, title: "New workspace session" })
        .select()
        .single();
      if (createErr) { setError(createErr.message); setThreadInitializing(false); return; }
      setThreadId(created.id);
      setWorkspaceStatus("Ready for blueprints, scopes, claims, and photos.");
      setThreadInitializing(false);
    })();
  }, [orgId, userId]);

  const loadThread = useCallback(async (id: string) => {
    const [{ data: msgs }, { data: fs }] = await Promise.all([
      supabase.from("bridgette_workspace_messages").select("*").eq("thread_id", id).order("created_at"),
      supabase.from("bridgette_workspace_files").select("*").eq("thread_id", id).order("created_at"),
    ]);
    setMessages((msgs ?? []) as WSMessage[]);
    setFiles((fs ?? []) as WSFile[]);
  }, []);

  const uploadFile = useCallback(async (file: File, category: string) => {
    if (!orgId || !userId) { const msg = "Organization context is missing."; setError(msg); toast.error(msg); return null; }
    if (!threadId) { const msg = "Workspace is still preparing. Try again in a second."; setError(msg); toast.error(msg); return null; }
    setWorkspaceStatus(`Uploading ${file.name}…`);
    const path = `${orgId}/${userId}/${threadId}/${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("bridgette-workspace").upload(path, file);
    if (upErr) { setError(upErr.message); setWorkspaceStatus("Upload failed."); toast.error(`Upload failed: ${upErr.message}`); return null; }

    const { data: row, error: insErr } = await supabase
      .from("bridgette_workspace_files")
      .insert({
        thread_id: threadId,
        org_id: orgId,
        uploaded_by: userId,
        file_name: file.name,
        file_type: file.type,
        storage_path: path,
        size_bytes: file.size,
        category,
      })
      .select()
      .single();
    if (insErr) { setError(insErr.message); setWorkspaceStatus("Upload saved, but file indexing failed."); toast.error(`File indexing failed: ${insErr.message}`); return null; }

    setFiles((prev) => [...prev, row as WSFile]);
    setWorkspaceStatus(`Analyzing ${file.name}…`);
    toast.success(`${file.name} uploaded. Analysis started.`);

    // Background ingest: function returns immediately, writes to row when done.
    // Subscribe to the row UPDATE so we render results without blocking.
    const channel = supabase
      .channel(`bridgette-workspace-file-${row.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "bridgette_workspace_files", filter: `id=eq.${row.id}` },
        (payload: any) => {
          const updated = payload.new as WSFile;
          const status = (updated?.extracted_data as any)?.ingest_status;
          if (status === "processing") return;
          const analyzedFile = { ...(row as WSFile), vision_summary: updated.vision_summary, extracted_data: updated.extracted_data };
          const assistantMessage = buildFileReadyMessage(analyzedFile, updated.vision_summary);
          const attachments = [buildScopeReviewAttachment(analyzedFile, updated.vision_summary)];
          setFiles((prev) => prev.map((f) => f.id === row.id ? analyzedFile : f));
          setMessages((prev) => [...prev, { role: "assistant", content: assistantMessage, attachments }]);
          void supabase.from("bridgette_workspace_messages").insert({
            thread_id: threadId,
            org_id: orgId,
            role: "assistant",
            content: assistantMessage,
            attachments,
          });
          setWorkspaceStatus(status === "error" ? "Analysis failed. Try uploading again." : "Analysis complete. Bridgette can use the file now.");
          supabase.removeChannel(channel);
        }
      )
      .subscribe();

    supabase.functions.invoke("bridgette-workspace-ingest", { body: { file_id: row.id } })
      .then(({ error }: any) => {
        if (error) {
          setWorkspaceStatus("Upload complete. Analysis needs retry.");
          toast.error(error?.message ?? "File analysis failed to start");
          supabase.removeChannel(channel);
        }
      })
      .catch((e: any) => {
        setWorkspaceStatus("Upload complete. Analysis needs retry.");
        toast.error(e?.message ?? "File analysis failed");
        supabase.removeChannel(channel);
      });

    return row as WSFile;
  }, [orgId, userId, threadId]);

  const sendMessage = useCallback(async (text: string) => {
    if (!threadId || !orgId || !text.trim()) return;
    setError(null);
    const userMsg: WSMessage = { role: "user", content: text };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs);

    await supabase.from("bridgette_workspace_messages").insert({
      thread_id: threadId, org_id: orgId, role: "user", content: text,
    });

    setIsStreaming(true);
    abortRef.current = new AbortController();

    try {
      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp.data.session?.access_token ?? "";
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bridgette-workspace-chat`;

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          thread_id: threadId,
          messages: nextMsgs.map((m) => ({ role: m.role, content: m.content })),
          files,
          current_route: window.location.pathname,
        }),
        signal: abortRef.current.signal,
      });

      if (!resp.ok || !resp.body) {
        const t = await resp.text();
        setError(`AI error: ${resp.status}`);
        console.error("workspace chat err", t);
        setIsStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      const streamedToolCalls: any[] = [];
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") break;
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            const toolDeltas = parsed.choices?.[0]?.delta?.tool_calls;
            if (Array.isArray(toolDeltas)) {
              toolDeltas.forEach((toolCall: any) => {
                const index = toolCall.index ?? 0;
                streamedToolCalls[index] = streamedToolCalls[index] ?? { id: toolCall.id, type: toolCall.type, function: { name: "", arguments: "" } };
                if (toolCall.id) streamedToolCalls[index].id = toolCall.id;
                if (toolCall.type) streamedToolCalls[index].type = toolCall.type;
                if (toolCall.function?.name) streamedToolCalls[index].function.name += toolCall.function.name;
                if (toolCall.function?.arguments) streamedToolCalls[index].function.arguments += toolCall.function.arguments;
              });
            }
            if (delta) {
              assistantText += delta;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: assistantText, tool_calls: streamedToolCalls.filter(Boolean) };
                return copy;
              });
            }
          } catch { /* partial */ }
        }
      }

      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: assistantText, tool_calls: streamedToolCalls.filter(Boolean) };
        return copy;
      });

      await supabase.from("bridgette_workspace_messages").insert({
        thread_id: threadId, org_id: orgId, role: "assistant", content: assistantText, tool_calls: streamedToolCalls.filter(Boolean),
      });
    } catch (e: any) {
      if (e?.name !== "AbortError") setError(e?.message ?? "Stream error");
    } finally {
      setIsStreaming(false);
    }
  }, [threadId, orgId, messages, files]);

  useEffect(() => {
    const handler = (event: Event) => {
      const utterance = (event as CustomEvent<{ utterance?: string }>).detail?.utterance;
      if (utterance?.trim()) void sendMessage(utterance);
    };
    window.addEventListener("bridgette-workspace-voice-command", handler);
    return () => window.removeEventListener("bridgette-workspace-voice-command", handler);
  }, [sendMessage]);

  const newSession = useCallback(async () => {
    if (!orgId || !userId) return;
    const { data: created } = await supabase
      .from("bridgette_workspace_threads")
      .insert({ org_id: orgId, created_by: userId, title: "New workspace session" })
      .select()
      .single();
    if (created) {
      setThreadId(created.id);
      setMessages([]);
      setFiles([]);
    }
  }, [orgId, userId]);

  return { threadId, messages, files, isStreaming, threadInitializing, workspaceReady: Boolean(threadId && orgId && userId), workspaceStatus, error, sendMessage, uploadFile, newSession };
}
